import * as React from 'react';
import ReactDOM from 'react-dom';

import debounce from 'lodash/debounce';

import { createReactRenderer } from 'helpers/react-renderer';
import AjaxMessagesHandler from 'root/apps/PurchaseOrder/LandedCostControl/AjaxMessagesHandler';
import { DiscountHandler } from 'root/apps/PurchaseOrder/LandedCostControl/DiscountHandler';
import { DiscountHelpDisplay } from 'root/apps/PurchaseOrder/LandedCostControl/DiscountHelpDisplay';
import SectionHelp from 'root/apps/PurchaseOrder/LandedCostControl/SectionHelp';
import { ShipCostHandler } from 'root/apps/PurchaseOrder/LandedCostControl/ShipCostHandler';
import {
  DiscountMethod,
  ShippingCostMethod,
} from 'root/apps/PurchaseOrder/LandedCostControl/types';

interface Props {
  discountMethod?: DiscountMethod;
  discountIsPercent?: 0 | 1 | boolean;
  domQueryDiscountMethodSelect?: string;
  domQueryDiscountIsPercentSelect?: string;
  domQueryDiscountPercentValueInput?: string;
  domQueryDiscountMoneyValueInput?: string;
  shippingCostMethod?: ShippingCostMethod;
  domQueryShippingCostMethodSelect?: string;
  domQueryShipCostStaticText?: string;
  domQueryShipCostInput?: string;
  domQueryAjaxMessagesDisplay?: string;
  domQueryDiscountHelpDisplay?: string;
  domQueryCostFieldGroup?: string;
  poStatus?: string | undefined;
}

const LandedCostControl: React.FC<Props> = ({
  discountMethod = 'evenly',
  discountIsPercent = true,
  domQueryDiscountMethodSelect = '[name="discount_method"]',
  domQueryDiscountIsPercentSelect = '[name="discount_is_percent"]',
  domQueryDiscountPercentValueInput = '[name="discount_percent_value"]',
  domQueryDiscountMoneyValueInput = '[name="discount_money_vendor_value"]',
  shippingCostMethod = 'total',
  domQueryShippingCostMethodSelect = '[name="shipping_cost_method"]',
  domQueryShipCostStaticText = '.ship_cost_static_text',
  domQueryShipCostInput = '.ship_cost_input_field',
  domQueryAjaxMessagesDisplay = '.ajax_messages_display',
  domQueryDiscountHelpDisplay = '.discount_help_display',
  domQueryCostFieldGroup = '#po_cost_field_group',
  poStatus,
}) => {
  const [discountMethodSelect] = React.useState<HTMLSelectElement | null>(
    document.querySelector(domQueryDiscountMethodSelect) as HTMLSelectElement | null
  );
  const [discountIsPercentSelect] = React.useState<HTMLSelectElement | null>(
    document.querySelector(domQueryDiscountIsPercentSelect) as HTMLSelectElement | null
  );
  const [discountPercentValueInput] = React.useState<HTMLInputElement | null>(
    document.querySelector(domQueryDiscountPercentValueInput) as HTMLInputElement | null
  );
  const [discountMoneyValueInput] = React.useState<HTMLInputElement | null>(
    document.querySelector(domQueryDiscountMoneyValueInput) as HTMLInputElement | null
  );

  const [shippingMethodSelect] = React.useState<HTMLSelectElement | null>(
    document.querySelector(domQueryShippingCostMethodSelect) as HTMLSelectElement | null
  );
  const [shipCostStaticText] = React.useState<HTMLDivElement | null>(
    document.querySelector(domQueryShipCostStaticText) as HTMLDivElement | null
  );
  const [shipCostInput] = React.useState<HTMLInputElement | null>(
    document.querySelector(domQueryShipCostInput) as HTMLInputElement | null
  );

  const [ajaxMessagesDisplay] = React.useState<HTMLDivElement | null>(
    document.querySelector(domQueryAjaxMessagesDisplay) as HTMLDivElement | null
  );

  const [discountHelpDisplay] = React.useState<HTMLDivElement | null>(
    document.querySelector(domQueryDiscountHelpDisplay) as HTMLDivElement | null
  );

  const RenderDiscountHandler = () => {
    return discountMethodSelect &&
      discountIsPercentSelect &&
      discountPercentValueInput &&
      discountMoneyValueInput ? (
      <DiscountHandler
        discountMethod={discountMethod}
        discountIsPercent={discountIsPercent}
        discountMethodSelect={discountMethodSelect}
        discountIsPercentSelect={discountIsPercentSelect}
        discountPercentValueInput={discountPercentValueInput}
        discountMoneyValueInput={discountMoneyValueInput}
        status={poStatus}
      />
    ) : null;
  };

  const RenderShipCostHandler = () => {
    return shippingMethodSelect && shipCostStaticText && shipCostInput ? (
      <ShipCostHandler
        shippingCostMethod={shippingCostMethod}
        shippingMethodSelect={shippingMethodSelect}
        shipCostStaticText={shipCostStaticText}
        shipCostInput={shipCostInput}
      />
    ) : null;
  };

  const RenderAjaxMessagesListener = () => {
    return ajaxMessagesDisplay ? (
      <AjaxMessagesHandler ajaxMessagesDisplay={ajaxMessagesDisplay} />
    ) : null;
  };

  const RenderDiscountHelpDisplay = () => {
    return discountHelpDisplay ? (
      <DiscountHelpDisplay
        discountHelpDisplay={discountHelpDisplay}
        discountMethod={discountMethod}
        status={poStatus}
      />
    ) : null;
  };

  React.useEffect(() => {
    if (!domQueryCostFieldGroup) return () => {};

    const targetDOM = document.querySelector(domQueryCostFieldGroup) as HTMLElement;
    if (!targetDOM) return () => {};

    targetDOM.style.position = 'relative';
    const div = document.createElement('div');
    targetDOM.appendChild(div);

    ReactDOM.render(<SectionHelp />, div);

    return () => {
      ReactDOM.unmountComponentAtNode(div);
      targetDOM.removeChild(div);
    };
  }, [domQueryCostFieldGroup]);

  return (
    <>
      <RenderDiscountHandler />
      <RenderShipCostHandler />
      <RenderAjaxMessagesListener />
      <RenderDiscountHelpDisplay />
    </>
  );
};

const RenderedComponent = createReactRenderer({
  component: LandedCostControl,
  selector: '[data-react-root="LandedCostControl"]',
});

export default LandedCostControl;

/**
 * Allow the Costs' contextual save button to only react to fields under the Costs field group.
 * Bind an extra event on all fields to trigger a has_changed check on the Costs fields.
 * Using namespace to avoid unbinding other events.
 * Calling off() before on() to avoid multiple bindings.
 * Using debounce to ensure the check is done after the common check on the view.
 */
const bindCheckCostGroupChanges = () => {
  const debouncedCheckPannelForChanges = debounce(() => {
    window.checkPannelForChanges('po_cost_field_group');
  }, 10);
  $('#view [onchange*="viewFieldChanged"]')
    .off('change.checkCostGroupChanges')
    .on('change.checkCostGroupChanges', () => {
      debouncedCheckPannelForChanges();
    });
  $('#view [onkeyup*="viewFieldChanged"]')
    .off('keyup.checkCostGroupChanges')
    .on('keyup.checkCostGroupChanges', () => {
      debouncedCheckPannelForChanges();
    });
};
const unbindCheckCostGroupChanges = () => {
  $('#view [onchange*="viewFieldChanged"]').off('change.checkCostGroupChanges');
  $('#view [onkeyup*="viewFieldChanged"]').off('keyup.checkCostGroupChanges');
};

history.onChangeState(
  [{ name: ['purchase.views.purchase'], form_name: 'view', id: undefined }],
  () => {
    RenderedComponent.mount();
    bindCheckCostGroupChanges();
  },
  () => {
    RenderedComponent.unmount();
    unbindCheckCostGroupChanges();
  }
);
